import React, { useMemo, useState } from "react";

import styled from "styled-components";

import { Popup, Table } from "simplydo/react/ui";

import { useAppSelector } from "store";

import { LargeCheckbox } from "components/lib/UI";

import ActionDropdown, { type Action } from "./ActionDropdown";
import type { Column, TableRowItem } from "./types";

const actionKey = "__actions";
const minCellWidth = 75;

const SizedDataCell = styled(Table.Cell)<{
  $preferredWidth?: number;
  $isActionCell?: boolean;
  $isDropdownOpen?: boolean;
}>`
  padding: 8px 0;
  background: #fff;

  ${({ $preferredWidth }) =>
    !$preferredWidth &&
    `
    min-width: ${minCellWidth}px;
  `}

  ${({ $isActionCell, $isDropdownOpen }) =>
    $isActionCell ?
      `
    position: sticky;
    right: -1px;
    min-width: 85px;
    width: 85px;
    box-shadow: -1px 0 1px #ddd;
    z-index: ${$isDropdownOpen ? 1 : 0};
  `
    : null}
`;

const CellContentWrapper = styled.div<{ $lines?: number; $center?: boolean }>`
  display: flex;
  flex: 1;
  justify-content: ${({ $center }) => ($center ? "center" : "flex-start")};
  align-items: ${({ $center }) => ($center ? "center" : "flex-start")};
  width: 100%;

  ${({ $lines }) =>
    $lines &&
    `
    -webkit-line-clamp: ${$lines};
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const StyledRow = styled(Table.Row)<{ $disabled?: boolean }>`
  ${({ $disabled }) =>
    $disabled &&
    `
  color: grey;
  opacity: 0.8;
  background-color: #f9f9f9;
  pointer-events: none;
`}

  &:hover {
    ${SizedDataCell} {
      background: #f5f5f5 !important;
    }
  }
`;

const clickEventTargetIsInteractable = (target: HTMLElement) => {
  const tagName = (target.tagName || "").toLowerCase();

  if (
    tagName === "button" ||
    tagName === "a" ||
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    tagName === "option"
  ) {
    return true;
  }

  const clases = target.classList;
  if (
    clases.contains("popup") ||
    clases.contains("modal") ||
    clases.contains("label") ||
    clases.contains("chip")
  ) {
    return true;
  }

  if (!target.parentElement) {
    return false;
  }

  return clickEventTargetIsInteractable(target.parentElement);
};

type DataRowProps<T extends TableRowItem> = {
  onRowClick?: (item: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
  item: T;
  selectable: boolean;
  selected: boolean;
  enabledColumns: Column<T>[];
  onSelect?: (item: T, checked: boolean) => void;
  actions?: Action<T>[];
  index: number;
  getRowStyle?: (item: T) => React.CSSProperties;
  getCellStyle?: (cell: Column<T>, item: T) => React.CSSProperties;
};

const DataRow = <T extends TableRowItem>({
  onRowClick,
  item,
  selectable,
  selected,
  enabledColumns,
  onSelect,
  actions = [],
  index,
  getRowStyle,
  getCellStyle,
}: DataRowProps<T>) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const actionColumnItems = useMemo(() => [item], [item]);

  const user = useAppSelector((state) => state.user);

  return (
    <StyledRow
      $disabled={item.disabled}
      style={getRowStyle ? getRowStyle(item) : {}}
      onMouseDown={() => {
        if (window.getSelection) {
          window.getSelection()?.removeAllRanges();
        }
      }}
      onClick={(event: React.MouseEvent<HTMLTableRowElement>) => {
        if (event.defaultPrevented) {
          return;
        }
        const selection = window.getSelection();
        if (selection?.toString()) {
          return;
        }
        if (clickEventTargetIsInteractable(event.target as HTMLElement)) {
          return;
        }
        onRowClick?.(item, event);
      }}
    >
      {selectable ?
        <SizedDataCell
          $preferredWidth={40}
          style={
            getCellStyle ?
              getCellStyle({ key: "__select" } as Column<T>, item)
            : {}
          }
        >
          <div
            style={{
              display: "flex",
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {!item.disabled && (
              <LargeCheckbox
                checked={selected}
                onChange={(e, checked) => {
                  e.preventDefault();
                  onSelect?.(item, checked);
                }}
              />
            )}
          </div>
        </SizedDataCell>
      : null}
      {enabledColumns.map((col) => {
        const cellValue = item[col.key as keyof T];
        const content =
          col.render ?
            col.render({ cell: cellValue, item })
          : (cellValue as React.ReactNode);
        return (
          <SizedDataCell
            $preferredWidth={col.width}
            $isActionCell={col.key === actionKey}
            $isDropdownOpen={dropdownOpen}
            collapsing={col.key === actionKey || col.collapsing}
            key={col.key}
            style={{
              ...(col.style || {}),
              ...(getCellStyle ? getCellStyle(col, item) : {}),
            }}
          >
            <CellContentWrapper $lines={col.lines} $center={col.center}>
              {col.key === actionKey ?
                <ActionDropdown
                  actions={actions}
                  items={actionColumnItems}
                  type="single"
                  user={user}
                  setDropdownOpen={setDropdownOpen}
                />
              : col.lines ?
                <Popup
                  hoverable
                  trigger={<span>{content}</span>}
                  content={<span>{content}</span>}
                />
              : content}
            </CellContentWrapper>
          </SizedDataCell>
        );
      })}
    </StyledRow>
  );
};

export default React.memo(DataRow) as typeof DataRow;
